import { useState } from "react";
import { Eye, EyeOff, Pencil, Trash2, Check, X, Plus, TrendingUp, RotateCcw } from "lucide-react";
import { JOURNEY_CHANNELS, channelMeta } from "./journeyChannels";

const CampaignItem = ({ item, isNew, readOnly, onRename, onToggle, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const meta = channelMeta(item.channel);
  const submit = () => {
    const n = name.trim();
    if (n && n !== item.name) onRename(n === item.original_name && !item.custom ? "" : n);
    setEditing(false);
  };
  return (
    <li
      className={`flex items-center gap-2 rounded-lg bg-slate-900/40 border border-white/5 px-2.5 py-1.5 ${item.hidden ? "opacity-45" : ""}`}
      data-testid={`journey-campaign-item-${item.key}`}
    >
      <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-slate-400" title={meta.label} />
      {editing ? (
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setEditing(false); }}
          className="flex-1 min-w-0 bg-slate-950/60 border border-lime-300/40 rounded px-2 py-0.5 text-[12px] text-white outline-none"
          data-testid="journey-campaign-rename-input"
        />
      ) : (
        <span className="flex-1 min-w-0 text-[12px] text-slate-100 truncate" title={item.name}>
          {isNew && <span className="mr-1.5 rounded bg-sky-300/20 text-sky-200 px-1 text-[9px] font-bold uppercase" data-testid="journey-campaign-new-tag">New</span>}
          {item.name}
          {item.hidden && <span className="ml-1.5 text-[10px] text-slate-400 italic">hidden from client</span>}
        </span>
      )}
      {!readOnly && (
        <span className="flex items-center gap-1 shrink-0 text-slate-400">
          {editing ? (
            <>
              <button type="button" onClick={submit} className="hover:text-lime-300" data-testid="journey-campaign-rename-save" aria-label="Save name"><Check className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => setEditing(false)} className="hover:text-white" aria-label="Cancel"><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <button type="button" onClick={() => { setName(item.name); setEditing(true); }} className="hover:text-white" data-testid="journey-campaign-rename-btn" aria-label="Rename"><Pencil className="w-3.5 h-3.5" /></button>
          )}
          {!editing && item.renamed && (
            <button type="button" onClick={() => onRename("")} className="hover:text-white" data-testid="journey-campaign-reset-name-btn" aria-label="Reset to original name" title={`Reset to "${item.original_name}"`}><RotateCcw className="w-3.5 h-3.5" /></button>
          )}
          {item.custom ? (
            <button type="button" onClick={onRemove} className="hover:text-white" data-testid="journey-campaign-delete-btn" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
          ) : (
            <button type="button" onClick={onToggle} className="hover:text-white" data-testid="journey-campaign-hide-btn" aria-label={item.hidden ? "Show to client" : "Hide from client"}>
              {item.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          )}
        </span>
      )}
    </li>
  );
};

const AddCampaign = ({ onAdd }) => {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("facebook");
  const submit = () => { if (!name.trim()) return; onAdd(name.trim(), channel); setName(""); };
  return (
    <div className="flex flex-col sm:flex-row gap-2 mt-3" data-testid="journey-add-campaign-form">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="Add a campaign that ran this month"
        className="flex-1 min-w-0 bg-slate-950/50 border border-white/10 rounded-lg px-3 py-1.5 text-[12px] text-white placeholder:text-slate-500 outline-none focus:border-lime-300/50"
        data-testid="journey-add-campaign-name"
      />
      <select
        value={channel}
        onChange={(e) => setChannel(e.target.value)}
        className="bg-slate-950/50 border border-white/10 rounded-lg px-2 py-1.5 text-[12px] text-white outline-none"
        data-testid="journey-add-campaign-channel"
      >
        {Object.entries(JOURNEY_CHANNELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </select>
      <button
        type="button"
        onClick={submit}
        className="inline-flex items-center justify-center gap-1 rounded-lg bg-lime-300 hover:bg-lime-200 text-slate-900 text-[12px] font-bold px-3 py-1.5 transition-colors"
        data-testid="journey-add-campaign-submit"
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
};

export const MonthCampaignPanel = ({ month, label, visits, spikePct, prevLabel, items, newKeys, readOnly, actions, fmtTotal }) => {
  const visible = readOnly ? items.filter((i) => !i.hidden) : items;
  return (
    <div className="mt-3 rounded-xl bg-slate-900/50 border border-lime-300/25 p-4" data-testid="journey-month-panel">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
        <p className="text-white font-bold text-sm" data-testid="journey-month-panel-title">{label}</p>
        <p className="text-slate-400 text-[12px] tabular-nums">{fmtTotal(visits)} website visits</p>
        {spikePct != null && (
          <p className="inline-flex items-center gap-1 text-lime-300 text-[12px] font-semibold" data-testid="journey-month-panel-spike">
            <TrendingUp className="w-3.5 h-3.5" /> Up {Math.round(spikePct)}% from {prevLabel}. Here's what was running.
          </p>
        )}
      </div>
      {visible.length === 0 ? (
        <p className="text-slate-400 text-[12px]">No paid campaigns recorded for this month.</p>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {visible.map((it) => (
            <CampaignItem
              key={it.key}
              item={it}
              isNew={newKeys?.has(it.key)}
              readOnly={readOnly}
              onRename={(n) => actions.rename(month, it.key, n)}
              onToggle={() => actions.toggleHidden(month, it.key, !it.hidden)}
              onRemove={() => actions.remove(month, it.key)}
            />
          ))}
        </ul>
      )}
      {!readOnly && <AddCampaign onAdd={(n, c) => actions.add(month, n, c)} />}
    </div>
  );
};

export default MonthCampaignPanel;
